import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/format";
import {
  MATCH_LINE_STATES,
  MATCH_LINE_STATE_FILTERS,
  MATCH_LINE_STATE_FILTER_VALUES,
  LINE_BUCKETS,
  humanizeEnumValue,
  verdictBadge,
  type MatchLineStateFilter,
} from "@/lib/matching";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type Row = components["schemas"]["MatchLineAdminRow"];

// Uncached on the server, unlike the tiles: this is a work queue, and a stale line sends someone to done work.
// Tabs come from the generated enum; the Record type on MATCH_LINE_STATE_FILTERS breaks the build until the gloss catches up.
const FILTERS = MATCH_LINE_STATE_FILTER_VALUES;

// Matches the endpoint default; bigger than the usual 20 because this is a backlog worked through.
const PAGE_SIZE = 100;

// Exactly one scope: the endpoint 400s without organisationId or clientId (it would load every match group).
type Scope =
  | { organisationId: string; clientId?: never }
  | { clientId: string; organisationId?: never };

export function MatchLinesTable({ organisationId, clientId }: Scope) {
  const navigate = useNavigate();
  const [state, setState] = useState<MatchLineStateFilter>("Actionable");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<Row[]>([]);
  const [cursors, setCursors] = useState<{ next?: string; previous?: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Changing the filter drops the cursor: it's a position in one filtered list.
  const changeState = (next: MatchLineStateFilter) => {
    setState(next);
    setCursor(undefined);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/admin/match-stats-lines", {
        params: {
          query: {
            OrganisationId: organisationId,
            ClientId: clientId,
            State: state,
            Cursor: cursor,
            Limit: PAGE_SIZE,
          },
        },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("The lines could not be loaded.");
          return;
        }
        setRows((data.data ?? []) as Row[]);
        setCursors({
          next: data.meta?.cursors?.next ?? undefined,
          previous: data.meta?.cursors?.previous ?? undefined,
        });
      })
      .catch(() => !cancelled && setError("The lines could not be loaded."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [organisationId, clientId, state, cursor]);

  // The match group with this line expanded: the only place showing what the engine offered and didn't take.
  const rowTarget = (r: Row) => `/match-groups/${r.matchGroupId}#line-${r.billLineId}`;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => changeState(f)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                state === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"
              }`}
            >
              {humanizeEnumValue(f)}
            </button>
          ))}
        </div>
        <p className="text-muted-foreground mt-1.5 text-xs">{MATCH_LINE_STATE_FILTERS[state]}</p>
      </div>

      {error ? (
        <div className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-3 rounded-lg border p-4 text-sm">
          <AlertTriangle className="mt-0.5 size-4" />
          <span>{error}</span>
        </div>
      ) : loading ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading lines…
        </div>
      ) : (
        <>
          <div className="border-border rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Line</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>
                    <span className="inline-flex items-center gap-1">
                      State
                      <InfoHint label="State" side="bottom">
                        <span className="block space-y-1">
                          {/* Driven off the generated values, not the gloss keys, so the legend can't fall behind the server. */}
                          {LINE_BUCKETS.map((value) => (
                            <span key={value} className="block">
                              <span className="font-semibold" title={value}>
                                {humanizeEnumValue(value)}
                              </span>{" "}
                              - {MATCH_LINE_STATES[value]}
                            </span>
                          ))}
                        </span>
                      </InfoHint>
                    </span>
                  </TableHead>
                  {/* Only populated on reviewer-made matches. */}
                  <TableHead>Why manual</TableHead>
                  <TableHead>Matched to</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Bill</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground text-center">
                      No lines in this state.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow
                      key={r.billLineId}
                      className="hover:bg-accent/50 cursor-pointer"
                      // Mouse convenience only: the last cell's link carries this for keyboard and screen readers.
                      // Let clicks on the cells' own links win.
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest("a,button")) return;
                        navigate(rowTarget(r));
                      }}
                    >
                      <TableCell className="max-w-xs truncate">{r.description}</TableCell>
                      <TableCell>
                        {/* A supplier name may have no id (never resolved): print it, link only when there's an id. */}
                        {r.supplierId ? (
                          <Link to={`/suppliers/${r.supplierId}`} className="hover:underline">
                            {r.supplierName ?? (
                              <span className="font-mono text-xs">{r.supplierId.slice(0, 8)}…</span>
                            )}
                          </Link>
                        ) : (
                          (r.supplierName ?? <span className="text-muted-foreground">-</span>)
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Humanised for reading; the raw enum value stays on the title for comparing against payloads. */}
                        <Badge variant="outline" className="text-[10px]" title={r.bucket}>
                          {humanizeEnumValue(r.bucket)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <WhyManual verdict={r.engineVerdict} />
                      </TableCell>
                      <TableCell className="space-y-0.5">
                        {r.purchaseOrders?.map((po) => (
                          <div key={po.id}>
                            <Link to={`/purchase-orders/${po.id}`} className="font-mono text-xs hover:underline">
                              PO {po.number}
                            </Link>
                          </div>
                        ))}
                        {r.deliveryNotes?.map((dn) => (
                          <div key={dn.id}>
                            <Link to={`/delivery-notes/${dn.id}`} className="font-mono text-xs hover:underline">
                              DN {dn.number}
                            </Link>
                          </div>
                        ))}
                        {!r.purchaseOrders?.length && !r.deliveryNotes?.length && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(r.billedNet, r.currency)}
                      </TableCell>
                      <TableCell>
                        {/* The bill-line id rides in the hash so the bill page can scroll to and highlight the row. */}
                        <Link
                          to={`/bills/${r.billId}#line-${r.billLineId}`}
                          className="font-mono text-xs hover:underline"
                        >
                          {r.billId.slice(0, 8)}…
                        </Link>
                        <span className="text-muted-foreground block text-xs">{r.matchGroupStatus}</span>
                      </TableCell>
                      {/* The row's only keyboard-reachable route to the match group. */}
                      <TableCell>
                        <Link
                          to={rowTarget(r)}
                          aria-label={`Open the match group for ${r.description}`}
                          className="text-muted-foreground hover:text-foreground focus-visible:text-foreground inline-flex"
                        >
                          <ChevronRight className="size-4" aria-hidden />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Always rendered, so a complete list doesn't look like missing paging. The count is this page's:
              the total is on the tile. */}
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              {rows.length} line{rows.length === 1 ? "" : "s"}
              {(cursors.previous || cursors.next) && " on this page"}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={!cursors.previous}
                onClick={() => setCursor(cursors.previous)}
                className="enabled:hover:bg-accent rounded-md px-2.5 py-1 font-medium transition disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!cursors.next}
                onClick={() => setCursor(cursors.next)}
                className="enabled:hover:bg-accent rounded-md px-2.5 py-1 font-medium transition disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Reason only, no number: engineTopConfidence is the score after the manual-link boost, not the one the verdict used.
function WhyManual({ verdict }: { verdict?: components["schemas"]["MatchAutoVerdict"] | null }) {
  const badge = verdictBadge(verdict);
  if (!badge) return <span className="text-muted-foreground">-</span>;

  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={badge.variant} className="text-[10px]">
        {badge.label}
      </Badge>
      <InfoHint label={badge.label}>{badge.hint}</InfoHint>
    </span>
  );
}
